import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { useAdminRole } from "@/hooks/useAdminRole";
import { MobileFrame } from "@/components/MobileFrame";
import {
  THEMES,
  THEME_IDS,
  THEME_TOKENS,
  enforceContrast,
  parseHsl,
  hslToRgb,
  contrastRatio,
  isSameFamily,
  hueDistance,
  SAME_FAMILY_HUE_THRESHOLD,
  type ThemeId,
  type ThemeTokens,
  FONT_SCALES,
  FONT_SCALE_LABELS,
  FONT_SCALE_VALUES,
} from "@/lib/themes";

// ─────────────────────────────────────────────────────────────────────────────
// Accessibility report — for every theme, walk every meaningful FG token
// against every surface it could be rendered on, and flag any pairing that
// either fails WCAG AA (4.5:1) or sits in the same hue family.
// Pure presentation page; no data fetching, no side effects.
// ─────────────────────────────────────────────────────────────────────────────

const SURFACE_TOKENS = [
  "paper", "paper-2", "paper-3", "cream",
  "ink", "rust", "rust-soft",
  "sand", "sand-deep", "olive", "sky", "plum", "marigold",
] as const;

// Foreground tokens that components may apply directly as text on a paper /
// neutral surface. We evaluate these only against paper-class surfaces — using
// a colored accent token as text on another colored accent surface is a
// component bug (the component should use the paired `{surface}-fg` token).
const PRIMARY_FG_TOKENS = ["ink", "ink-2", "rust", "sand-deep", "olive", "sky", "plum"] as const;
const PAPER_SURFACES = ["paper", "paper-2", "paper-3", "cream"] as const;
const ACCENT_SURFACES = ["rust", "rust-soft", "sand", "sand-deep", "olive", "sky", "plum", "marigold", "ink"] as const;

type Pair = {
  fg: string;
  bg: string;
  fgHsl: string;
  bgHsl: string;
  ratio: number;
  passesAA: boolean;
  sameFamily: boolean;
  hueDist: number;
};

// Build the list of pairings the design system actually produces:
//   - For paper-class surfaces: every primary FG token rendered on it.
//   - For accent-class surfaces: only the paired `{surface}-fg` token, since
//     that is the only correct way to put text on a colored surface.
function evaluate(tokens: ThemeTokens): Pair[] {
  const out: Pair[] = [];

  const pushPair = (fgKey: string, bgKey: string) => {
    if (fgKey === bgKey) return;
    const fgRaw = tokens[fgKey];
    const bgRaw = tokens[bgKey];
    const fgHsl = fgRaw ? parseHsl(fgRaw) : null;
    const bgHsl = bgRaw ? parseHsl(bgRaw) : null;
    if (!fgHsl || !bgHsl) return;
    const ratio = contrastRatio(
      hslToRgb(fgHsl[0], fgHsl[1], fgHsl[2]),
      hslToRgb(bgHsl[0], bgHsl[1], bgHsl[2]),
    );
    out.push({
      fg: fgKey, bg: bgKey,
      fgHsl: fgRaw, bgHsl: bgRaw,
      ratio,
      passesAA: ratio >= 4.5,
      sameFamily: isSameFamily(fgHsl, bgHsl),
      hueDist: hueDistance(fgHsl[0], bgHsl[0]),
    });
  };

  // 1) Primary FG tokens on paper-class surfaces (the most common case).
  for (const fg of PRIMARY_FG_TOKENS) {
    for (const bg of PAPER_SURFACES) pushPair(fg, bg);
  }

  // 2) For every accent surface, evaluate the paired `{surface}-fg` token —
  //    auto-derived by enforceContrast and guaranteed to clear AA.
  for (const bg of ACCENT_SURFACES) {
    pushPair(`${bg}-fg`, bg);
  }
  // Also evaluate paper surfaces' paired tokens (defensive — they should pass).
  for (const bg of PAPER_SURFACES) {
    pushPair(`${bg}-fg`, bg);
  }

  return out;
}

export default function SettingsAccessibility() {
  const nav = useNavigate();
  const { isAdmin, adminLoading } = useAdminRole();
  const [downloading, setDownloading] = useState(false);
  const reports = useMemo(() => {
    return THEME_IDS.map((id) => {
      const enforced = enforceContrast(THEME_TOKENS[id]);
      const pairs = evaluate(enforced);
      const failures = pairs.filter((p) => !p.passesAA || p.sameFamily);
      return { id: id as ThemeId, tokens: enforced, pairs, failures };
    });
  }, []);

  const downloadPdf = () => {
    setDownloading(true);
    try {
      generatePdf(reports);
    } finally {
      setDownloading(false);
    }
  };

  // Admin-only — the per-theme contrast report is a dev/QA tool. Hooks
  // above run for every visitor (they're cheap and side-effect-free); the
  // bounce only renders the "not authorized" page for non-admins.
  if (adminLoading) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: 80, color: "hsl(var(--ink-2))" }}>
          Checking access…
        </div>
      </MobileFrame>
    );
  }
  if (isAdmin === false) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: 80 }}>
          <h1 style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 28, color: "hsl(var(--ink))" }}>
            Not authorized
          </h1>
          <p style={{ marginTop: 8, color: "hsl(var(--ink-2))" }}>
            The accessibility report is a developer tool restricted to admins.
          </p>
          <button onClick={() => nav("/home")} className="btn" style={{ marginTop: 16, padding: "10px 14px" }}>
            Go home
          </button>
        </div>
      </MobileFrame>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--paper))", color: "hsl(var(--ink))" }}>
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 20px 64px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
          <h1 style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 28, margin: 0, lineHeight: 1.1,
          }}>
            Accessibility Report
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloading}
              style={{
                background: "hsl(var(--ink))",
                color: "hsl(var(--ink-fg))",
                border: "1px solid hsl(var(--ink))",
                padding: "8px 12px",
                cursor: downloading ? "wait" : "pointer",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                opacity: downloading ? 0.6 : 1,
              }}
            >
              {downloading ? "Generating…" : "Download PDF"}
            </button>
            <Link
              to="/settings"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                color: "hsl(var(--ink))",
                textDecoration: "underline",
              }}
            >
              ← Settings
            </Link>
          </div>
        </div>

        <p style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.5, marginTop: 0, marginBottom: 24,
        }}>
          Each theme is evaluated AFTER auto-contrast enforcement runs.
          Pairings flagged here would render if a component used the listed
          foreground token directly on the listed surface.
          Targets: WCAG AA contrast ≥ 4.5:1 and hue distance &gt; {SAME_FAMILY_HUE_THRESHOLD}°
          (so text is never just a tint of its background).
        </p>

        <FontScaleMatrix />

        {reports.map((r) => (
          <ThemeBlock key={r.id} report={r} />
        ))}
      </div>
    </div>
  );
}

// ─── Font-scale verification ────────────────────────────────────────────────
// Contrast ratios are size-independent, but WCAG AA's required ratio depends
// on rendered text size: 4.5:1 for normal text, 3:1 for "large" text
// (≥18pt / 24px regular, or ≥14pt / 18.66px bold). Since enforceContrast
// guarantees ≥4.5:1 against every paper surface for the primary text tokens,
// AA holds at every font scale automatically. This matrix shows the math.
function FontScaleMatrix() {
  // Body text = 14px in Fig (most components use 11–14px). Headings ≥ 16px.
  const BODY_PX = 14;
  const HEADING_PX = 16;
  return (
    <section style={{
      border: "1px solid hsl(var(--ink))",
      background: "hsl(var(--cream))",
      marginBottom: 20,
      padding: 16,
    }}>
      <div style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 18, fontWeight: 700, marginBottom: 4,
      }}>
        Font scale × WCAG AA threshold
      </div>
      <div style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 12, color: "hsl(var(--ink-2))", marginBottom: 10, lineHeight: 1.4,
      }}>
        Required ratio drops to 3:1 once text reaches 18.66px (large text per WCAG 2.1).
        Primary text tokens are enforced to ≥ 4.5:1 already, so all sizes pass.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid hsl(var(--ink))" }}>
            <th style={th}>Scale</th>
            <th style={{ ...th, textAlign: "right" }}>Multiplier</th>
            <th style={{ ...th, textAlign: "right" }}>Body @ {BODY_PX}px</th>
            <th style={{ ...th, textAlign: "right" }}>Heading @ {HEADING_PX}px</th>
            <th style={th}>AA threshold</th>
            <th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {FONT_SCALES.map((s) => {
            const m = FONT_SCALE_VALUES[s];
            const body = BODY_PX * m;
            const heading = HEADING_PX * m;
            const isLargeBody = body >= 18.66;
            const required = isLargeBody ? 3 : 4.5;
            return (
              <tr key={s} style={{ borderBottom: "1px solid hsla(var(--ink) / 0.15)" }}>
                <td style={td}>{FONT_SCALE_LABELS[s]}</td>
                <td style={{ ...td, textAlign: "right" }}>{m.toFixed(3)}×</td>
                <td style={{ ...td, textAlign: "right" }}>{body.toFixed(1)}px</td>
                <td style={{ ...td, textAlign: "right" }}>{heading.toFixed(1)}px</td>
                <td style={td}>≥ {required}:1</td>
                <td style={td}>
                  <span style={{ color: "hsl(var(--ink))" }}>
                    Pass — primary tokens guaranteed ≥ 4.5:1
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function ThemeBlock({ report }: { report: { id: ThemeId; tokens: ThemeTokens; pairs: Pair[]; failures: Pair[] } }) {
  const meta = THEMES[report.id];
  const passCount = report.pairs.length - report.failures.length;

  return (
    <section style={{
      border: "1px solid hsl(var(--ink))",
      background: "hsl(var(--cream))",
      marginBottom: 20,
      padding: 16,
    }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 20, fontWeight: 700, lineHeight: 1.2,
          }}>
            {meta.name}
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
            color: "hsl(var(--ink-2))",
          }}>
            {report.id} · {passCount}/{report.pairs.length} pairings clean
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {meta.preview.map((hsl, i) => (
            <span key={i} style={{
              width: 22, height: 22,
              background: `hsl(${hsl})`,
              border: "1px solid hsl(var(--ink))",
              display: "inline-block",
            }} />
          ))}
        </div>
      </header>

      {report.failures.length === 0 ? (
        <div style={{
          padding: "10px 12px",
          background: "hsl(var(--sand))",
          color: "hsl(var(--sand-fg))",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, letterSpacing: "0.05em",
        }}>
          All {report.pairs.length} foreground × surface pairings pass AA and have
          distinct hues.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid hsl(var(--ink))" }}>
              <th style={th}>Sample</th>
              <th style={th}>FG</th>
              <th style={th}>BG</th>
              <th style={{ ...th, textAlign: "right" }}>Ratio</th>
              <th style={{ ...th, textAlign: "right" }}>Δhue</th>
              <th style={th}>Issue</th>
            </tr>
          </thead>
          <tbody>
            {report.failures.map((p, i) => (
              <tr key={i} style={{ borderBottom: "1px solid hsla(var(--ink) / 0.15)" }}>
                <td style={td}>
                  <span style={{
                    display: "inline-block",
                    padding: "4px 8px",
                    background: `hsl(${p.bgHsl})`,
                    color: `hsl(${p.fgHsl})`,
                    border: "1px solid hsl(var(--ink))",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                  }}>
                    Aa
                  </span>
                </td>
                <td style={td}>{p.fg}</td>
                <td style={td}>{p.bg}</td>
                <td style={{ ...td, textAlign: "right", color: p.passesAA ? "hsl(var(--ink))" : "hsl(var(--rust))" }}>
                  {p.ratio.toFixed(2)}:1
                </td>
                <td style={{ ...td, textAlign: "right", color: p.sameFamily ? "hsl(var(--rust))" : "hsl(var(--ink))" }}>
                  {Math.round(p.hueDist)}°
                </td>
                <td style={td}>
                  {!p.passesAA && <span>fails AA</span>}
                  {!p.passesAA && p.sameFamily && <span> · </span>}
                  {p.sameFamily && <span>same hue family</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const th: React.CSSProperties = {
  padding: "6px 8px",
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  fontSize: 10,
};
const td: React.CSSProperties = {
  padding: "6px 8px",
  verticalAlign: "middle",
};

// ─── PDF export ─────────────────────────────────────────────────────────────
// Builds a multi-page PDF mirroring the on-screen report. jsPDF only — no
// html2canvas — so the output stays crisp and small. Colors are drawn from
// the same enforced HSL tokens used in the UI so the PDF reflects what the
// user would actually see.

type Report = { id: ThemeId; tokens: ThemeTokens; pairs: Pair[]; failures: Pair[] };

function hslTripletToRgb255(triplet: string): [number, number, number] {
  const hsl = parseHsl(triplet);
  if (!hsl) return [0, 0, 0];
  const [r, g, b] = hslToRgb(hsl[0], hsl[1], hsl[2]);
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function generatePdf(reports: Report[]) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN = 40;
  const CONTENT_W = PAGE_W - MARGIN * 2;
  let y = MARGIN;

  const ensureSpace = (needed: number) => {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  // Title page
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Fig — Accessibility Report", MARGIN, y);
  y += 24;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90);
  doc.text(
    `Generated ${new Date().toLocaleString()}  ·  ${reports.length} themes evaluated`,
    MARGIN,
    y,
  );
  y += 18;
  doc.setTextColor(0);
  const intro = doc.splitTextToSize(
    `Each theme is evaluated AFTER auto-contrast enforcement runs. Pairings flagged ` +
    `here would render if a component used the listed foreground token directly on the ` +
    `listed surface. Targets: WCAG AA contrast ≥ 4.5:1 and hue distance > ` +
    `${SAME_FAMILY_HUE_THRESHOLD}° between foreground and background.`,
    CONTENT_W,
  );
  doc.setFontSize(10);
  doc.text(intro, MARGIN, y);
  y += intro.length * 12 + 14;

  // Font scale matrix
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Font scale × WCAG AA threshold", MARGIN, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const scaleHeader = ["Scale", "Multiplier", "Body (px)", "Heading (px)", "AA threshold", "Status"];
  const scaleCols = [70, 70, 70, 80, 80, CONTENT_W - 370];
  drawTableRow(doc, MARGIN, y, scaleCols, scaleHeader, true);
  y += 16;
  for (const s of FONT_SCALES) {
    const m = FONT_SCALE_VALUES[s];
    const body = 14 * m;
    const heading = 16 * m;
    const required = body >= 18.66 ? "≥ 3:1" : "≥ 4.5:1";
    drawTableRow(doc, MARGIN, y, scaleCols, [
      FONT_SCALE_LABELS[s],
      `${m.toFixed(3)}x`,
      body.toFixed(1),
      heading.toFixed(1),
      required,
      "Pass — primary tokens ≥ 4.5:1",
    ], false);
    y += 14;
  }
  y += 14;

  // Per-theme blocks
  for (const r of reports) {
    const meta = THEMES[r.id];
    ensureSpace(60);
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
    y += 12;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(meta.name, MARGIN, y);
    // Preview swatches on the right
    let sx = MARGIN + CONTENT_W - 3 * 18 - 4;
    for (const hsl of meta.preview) {
      const [pr, pg, pb] = hslTripletToRgb255(hsl);
      doc.setFillColor(pr, pg, pb);
      doc.setDrawColor(0);
      doc.rect(sx, y - 12, 16, 16, "FD");
      sx += 18;
    }
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);
    const passCount = r.pairs.length - r.failures.length;
    doc.text(
      `${r.id}  ·  ${passCount}/${r.pairs.length} pairings clean  ·  ${r.failures.length} flagged`,
      MARGIN,
      y,
    );
    doc.setTextColor(0);
    y += 14;

    if (r.failures.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      const msg = `All ${r.pairs.length} foreground × surface pairings pass AA and have distinct hues.`;
      doc.text(msg, MARGIN, y);
      y += 18;
      continue;
    }

    // Failures table
    const cols = [50, 70, 70, 60, 50, CONTENT_W - 300];
    const header = ["Sample", "FG token", "BG token", "Ratio", "Δhue", "Issue"];
    ensureSpace(20);
    drawTableRow(doc, MARGIN, y, cols, header, true);
    y += 14;

    for (const p of r.failures) {
      ensureSpace(18);
      // Sample swatch (FG text on BG fill)
      const [br, bg_, bb] = hslTripletToRgb255(p.bgHsl);
      const [fr, fg_, fb] = hslTripletToRgb255(p.fgHsl);
      doc.setFillColor(br, bg_, bb);
      doc.setDrawColor(0);
      doc.rect(MARGIN, y - 10, cols[0] - 6, 14, "FD");
      doc.setTextColor(fr, fg_, fb);
      doc.setFontSize(9);
      doc.text("Aa", MARGIN + 6, y);
      doc.setTextColor(0);
      // Other cells
      const issueParts: string[] = [];
      if (!p.passesAA) issueParts.push("fails AA");
      if (p.sameFamily) issueParts.push("same hue family");
      drawTableRow(doc, MARGIN, y, cols, [
        "", // sample drawn already
        p.fg,
        p.bg,
        `${p.ratio.toFixed(2)}:1`,
        `${Math.round(p.hueDist)}°`,
        issueParts.join(" · "),
      ], false);
      y += 14;
    }
    y += 8;
  }

  doc.save("fig-accessibility-report.pdf");
}

function drawTableRow(
  doc: jsPDF,
  x: number,
  y: number,
  cols: number[],
  cells: string[],
  header: boolean,
) {
  doc.setFont("helvetica", header ? "bold" : "normal");
  doc.setFontSize(header ? 9 : 9);
  if (header) {
    doc.setDrawColor(0);
    doc.setLineWidth(0.4);
    doc.line(x, y + 3, x + cols.reduce((a, b) => a + b, 0), y + 3);
  }
  let cx = x;
  for (let i = 0; i < cells.length; i++) {
    if (cells[i]) {
      const text = doc.splitTextToSize(cells[i], cols[i] - 4)[0] ?? cells[i];
      doc.text(text, cx + 2, y);
    }
    cx += cols[i];
  }
}